import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromUsersStore from '../reducers/employee.reducer';
import * as fromUserGroupsSelectors from './employee-groups.selectors';
import { getEmployeeGroups } from './employee-groups.selectors';
import { Employee, EmployeeGroup, Invite } from '@fc-core';
import { getAllInvites, getInvitesLoading } from './invite.selectors';
import { FilterItem } from '@fc-vehicles/components/shared/vehicle-filter/vehicles-filter.component';
import { extractUsersGroups } from './employee.selectors.utils';
import { sortByPropertyWithOrder } from '@fc-shared/utils/sort-by-property';
import { selectRouteParams } from '@fc-store/selectors/router.selectors';
import * as fromEmployees from '@fc-store/reducers/employee.reducer';

export const getEmployeeState = createFeatureSelector<fromEmployees.State>(
  fromEmployees.employeesFeatureKey,
);
export const selectRawEmployees = createSelector(
  getEmployeeState,
  fromUsersStore.selectAll,
);

export const getEmployeeById = (id: string) =>
  createSelector(selectRawEmployees, (employees) =>
    employees.find((employee) => employee.id === id),
  );

export const getEmployeeSortBy = createSelector(
  getEmployeeState,
  fromUsersStore.getSortBy,
);
export const getEmployeeSortByList = createSelector(
  getEmployeeState,
  fromUsersStore.getSortByList,
);

export const getUserError = createSelector(
  getEmployeeState,
  fromUsersStore.getUserError,
);

export const getLoadings = createSelector(
  getEmployeeState,
  fromUsersStore.getLoading,
);

export const getEmployeeLoading = createSelector(
  getInvitesLoading,
  getLoadings,
  (inviteLoadings, userLoadings) => userLoadings || inviteLoadings,
);
export const getUserFilters = createSelector(
  getEmployeeState,
  fromUsersStore.getUserFilters,
);
export const getSelectedEmployeeFilters = createSelector(
  getEmployeeState,
  fromUsersStore.getSelectedEmployeeFilters,
);
export const getUsersSearchText = createSelector(
  getEmployeeState,
  fromUsersStore.getSearchText,
);
export const getUsersCount = createSelector(
  getEmployeeState,
  fromUsersStore.getUsersCount,
);

export const getCurrentEmployee = createSelector(
  getEmployeeState,
  fromUsersStore.getCurrentEmployee,
);

export const getUsersLoadProgress = createSelector(
  getUsersCount,
  selectRawEmployees,
  (userCount: number, allUsers: Employee[]) =>
    (allUsers?.length / userCount) * 100,
);

export const getSelectedUserGroupFilter = createSelector(
  getEmployeeState,
  fromUsersStore.getSelectedUserGroupFilter,
);
export const getInvites = createSelector(
  getAllInvites,
  getEmployeeGroups,
  (invites, userGroups) => {
    invites = invites.map((invite) => extractInvitesGroups(invite, userGroups));
    return invites;
  },
);

export const getOperators = createSelector(
  selectRawEmployees,
  (users: Employee[]) => users.filter((user) => user.operator),
);

export const getFormattedEmployees = createSelector(
  selectRawEmployees,
  getEmployeeGroups,
  getAllInvites,
  (employees, userGroups, invites) => {
    employees = extractUsersGroups(employees, userGroups);
    employees = employees.map((employee) => ({
      ...employee,
      hasInvite: invites.some((invite) => invite.employee === employee.id),
    }));
    return employees;
  },
);

export const getActiveEmployees = createSelector(
  getFormattedEmployees,
  (employees) => employees.filter((employee) => !employee.archived),
);

// list of filtered employees without selected group filter
export const getEmployeesWithoutGroupFilter = createSelector(
  getFormattedEmployees,
  getEmployeeSortBy,
  getUsersSearchText,
  getSelectedEmployeeFilters,
  (employees, sortBy, searchText, selectedFilters) => {
    employees = sortByPropertyWithOrder<Employee>(
      employees,
      sortBy.value,
      sortBy.order ?? false,
    );
    employees = searchUsers(employees, searchText);
    employees = filterUsersByState(employees, selectedFilters);
    return employees;
  },
);

// list of filtered employees
export const getEmployees = createSelector(
  getEmployeesWithoutGroupFilter,
  getSelectedUserGroupFilter,
  (employees, selectedUserGroup) => {
    return filterByUserGroup(employees, selectedUserGroup);
  },
);

// list of groups with count of employees
export const getEmployeeGroupsWithCount = createSelector(
  getEmployeesWithoutGroupFilter,
  getEmployeeGroups,
  (employees, groups) => {
    const allEmployeesGroup = {
      id: -1,
      name: `All employees (${employees?.length})`,
      color: '#B0BEC5',
    };

    const allGroups = groups?.map((group) => {
      return {
        ...group,
        name: `${group.name} (${
          employees.filter((user: Employee) => user?.groups?.includes(group.id))
            .length
        })`,
      };
    });
    return [allEmployeesGroup, ...allGroups];
  },
);

export const getSelectedEmployeeGroupWithCounter = createSelector(
  getSelectedUserGroupFilter,
  getEmployeesWithoutGroupFilter,
  (selectedUserGroup, employees) => {
    return !selectedUserGroup || selectedUserGroup.id === -1
      ? {
          name: `All employees (${employees.length})`,
          id: -1,
          color: '#B0BEC5',
        }
      : selectedUserGroup;
  },
);

export const filterByUserGroup = (
  users: Employee[],
  selectedUserGroup: EmployeeGroup,
): Employee[] => {
  if (selectedUserGroup?.id === -1) return users;
  return users.filter((user) => user.groups?.includes(selectedUserGroup?.id));
};

export const filterUsersByState = (
  employees: Employee[],
  employeeFilters: FilterItem[],
): Employee[] => {
  if (!employeeFilters?.length)
    return employees.filter((employee) => !employee.archived);
  const operator = employeeFilters.filter((i) => i.type === 'operator');
  const user = employeeFilters.filter((i) => i.type === 'user');
  const archived = employeeFilters.filter((i) => i.type === 'archived');
  const pending = employeeFilters.filter((i) => i.type === 'pending');
  const admin = employeeFilters.filter((i) => i.type === 'admin');
  if (pending.length)
    employees = employees.filter((employee) => employee.hasInvite);
  if (operator.length)
    employees = employees.filter((employee) => employee.operator);
  if (user.length) employees = employees.filter((employee) => employee.user);
  employees = employees.filter((employee) =>
    archived.length ? employee.archived : !employee.archived,
  );
  if (admin.length)
    employees = employees.filter((employee) => employee.isAdmin);
  return employees;
};

export const getEmployeesForCurrentUserGroup = createSelector(
  getFormattedEmployees,
  fromUserGroupsSelectors.getCurrentUserGroup,
  (employees, userGroup): Employee[] => {
    const groupEmployeeIds = userGroup?.employees;
    if (!groupEmployeeIds) return [];
    return employees.filter(
      (employee) => !!employee.id && groupEmployeeIds.includes(employee.id),
    );
  },
);

export const getInviteByEmployeeId = (employeeId: string) =>
  createSelector(getAllInvites, (invites) =>
    invites.find((invite) => invite.employee === employeeId),
  );

export const getSelectedEmployee = createSelector(
  selectRouteParams,
  getFormattedEmployees,
  ({ id }, employees) => {
    if (!id) return null;
    return employees.find((user) => user?.id === id);
  },
);

export const extractInvitesGroups = (
  invite: Invite,
  userGroups: EmployeeGroup[],
): Invite => {
  return {
    ...invite,
    fullGroups: userGroups.filter((group) => invite.groups?.includes(group.id)),
  };
};

export const searchUsers = (
  users: Employee[],
  searchText: string,
): Employee[] => {
  if (searchText?.trim().length) {
    const search = searchText?.trim()?.toLocaleLowerCase();
    return users.filter((vehicle) => {
      return [
        searchByPhone(vehicle, search),
        searchByName(vehicle, search),
        searchByEmail(vehicle, search),
      ].some((i) => i);
    });
  }
  return users;
};

const searchByPhone = (employee: Employee, search: string): boolean => {
  return !!(
    employee.primaryPhoneNumber?.toLocaleLowerCase().includes(search) ||
    employee.secondaryPhoneNumber?.toLocaleLowerCase().includes(search)
  );
};
const searchByName = (employee: Employee, search: string): boolean => {
  return !!employee.fullName?.toLocaleLowerCase().includes(search);
};
const searchByEmail = (employee: Employee, search: string): boolean => {
  return !!employee.email?.toLocaleLowerCase().includes(search);
};
